import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmationDialog, LoadingOverlayV2 } from '@/components/custom/erp';
import FormDialog from './components/form-dialog';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

type FormMode = 'add' | 'edit' | 'view';

interface DocumentEntryProps {
  document_uuid?: string;
  document_type?: string;
  formMode?: FormMode;
  initialData?: MuaHangRecord;
  onClose?: () => void;
  onSubmit?: (data: MuaHangRecord) => void | Promise<void>;
  onMutated?: () => void | Promise<void>;
}

interface EntryMutationState {
  crud: {
    addItem: (data: MuaHangRecord) => Promise<MuaHangRecord>;
    updateItem: (uuid: string, data: MuaHangRecord) => Promise<MuaHangRecord>;
    deleteItem: (uuid?: string) => Promise<void>;
    refreshData: () => Promise<void>;
  };
  selection: { selectedObj: MuaHangRecord; clearSelection: () => void };
  form: {
    formMode: FormMode;
    handleCloseForm: () => void;
    handleCloseDelete: () => void;
    handleEditClick: () => void;
  };
}

const EntryForm = FormDialog as ComponentType<MuaHangRecord>;

function useDocumentEntryMutation({ tableState }: { tableState: EntryMutationState }) {
  const handleSubmit = useCallback(
    async (values: MuaHangRecord) => {
      const data = values && typeof values === 'object' && 'data' in values ? values.data : values;
      const { formMode } = tableState.form;
      const selectedObj = tableState.selection.selectedObj;

      if (formMode === 'add') await tableState.crud.addItem(data);
      else if (formMode === 'edit' && selectedObj?.uuid) {
        await tableState.crud.updateItem(selectedObj.uuid, data);
      }

      await tableState.crud.refreshData();
      tableState.form.handleCloseForm();
    },
    [tableState]
  );

  const handleDelete = useCallback(async () => {
    await tableState.crud.deleteItem(tableState.selection.selectedObj?.uuid);
    tableState.form.handleCloseDelete();
    tableState.form.handleCloseForm();
    await tableState.crud.refreshData();
  }, [tableState]);

  const handleEditClick = useCallback(() => tableState.form.handleEditClick(), [tableState]);
  return { handleSubmit, handleDelete, handleEditClick };
}

function DocumentEntryContent({
  document_uuid,
  formMode: initialFormMode = 'view',
  initialData,
  onClose,
  onSubmit,
  onMutated
}: DocumentEntryProps) {
  const [formMode, setFormMode] = useState<FormMode>(initialFormMode);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [entryDetail, setEntryDetail] = useState<MuaHangRecord>(initialData);
  const [loadedDocumentUuid, setLoadedDocumentUuid] = useState<string | null>(null);
  const isDeletingRef = useRef(false);
  const latestSavedDetailRef = useRef<MuaHangRecord>(initialData);
  const lastFetchedUuidRef = useRef<string | null>(null);
  const { fetchDetail, addItem, updateItem, deleteItem } = useCRUD({
    endpoint: QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG,
    initialFetch: false
  });

  useEffect(() => {
    setEntryDetail(initialData);
    latestSavedDetailRef.current = initialData;
    setFormMode(initialFormMode);
    setIsCopyMode(false);
    lastFetchedUuidRef.current = null;
    setLoadedDocumentUuid(null);
  }, [document_uuid, initialData, initialFormMode]);

  const selectedObj = entryDetail ?? initialData;
  const formInitialData = formMode === 'add' && !isCopyMode ? undefined : selectedObj;
  const isDocumentDetailPending = Boolean(
    document_uuid &&
    formMode !== 'add' &&
    !isCopyMode &&
    (!selectedObj || selectedObj?.uuid === document_uuid) &&
    loadedDocumentUuid !== document_uuid
  );

  const handleDialogClose = useCallback(() => onClose?.(), [onClose]);
  const handleMutationCloseForm = useCallback(() => {
    onClose?.();
  }, [onClose]);
  const handleRefreshData = useCallback(async () => {
    await onMutated?.();
    await onSubmit?.(latestSavedDetailRef.current);
  }, [onMutated, onSubmit]);
  const handleFetchDetail = useCallback(
    async (uuid: string) => {
      const value = await fetchDetail(uuid);
      setEntryDetail(value);
      latestSavedDetailRef.current = value;
      setLoadedDocumentUuid(uuid);
      return value;
    },
    [fetchDetail]
  );
  useEffect(() => {
    if (!document_uuid || formMode === 'add' || isCopyMode || lastFetchedUuidRef.current === document_uuid) return;

    lastFetchedUuidRef.current = document_uuid;
    void handleFetchDetail(document_uuid);
  }, [document_uuid, formMode, handleFetchDetail, isCopyMode]);
  const handleAddItem = useCallback(
    async (data: MuaHangRecord) => {
      const value = await addItem(data);
      setEntryDetail(value);
      latestSavedDetailRef.current = value;
      return value;
    },
    [addItem]
  );
  const handleUpdateItem = useCallback(
    async (uuid: string, data: MuaHangRecord) => {
      const value = await updateItem(uuid, data);
      setEntryDetail(value);
      latestSavedDetailRef.current = value;
      return value;
    },
    [updateItem]
  );
  const handleDeleteItem = useCallback(
    async (uuid?: string) => {
      if (!uuid) return;
      await deleteItem(uuid);
      setEntryDetail(null);
      latestSavedDetailRef.current = null;
    },
    [deleteItem]
  );

  const tableState = useMemo<EntryMutationState>(
    () => ({
      crud: {
        addItem: handleAddItem,
        updateItem: handleUpdateItem,
        deleteItem: handleDeleteItem,
        refreshData: handleRefreshData
      },
      selection: { selectedObj, clearSelection: () => undefined },
      form: {
        formMode,
        handleCloseForm: handleMutationCloseForm,
        handleCloseDelete: () => setShowDelete(false),
        handleEditClick: () => {
          setIsCopyMode(false);
          setFormMode('edit');
        }
      }
    }),
    [
      formMode,
      handleAddItem,
      handleDeleteItem,
      handleMutationCloseForm,
      handleRefreshData,
      handleUpdateItem,
      selectedObj
    ]
  );
  const mutationState = useDocumentEntryMutation({ tableState });

  const handleConfirmDelete = useCallback(async () => {
    isDeletingRef.current = true;
    try {
      await mutationState.handleDelete();
    } finally {
      isDeletingRef.current = false;
    }
  }, [mutationState]);

  if (isDocumentDetailPending) {
    return (
      <div className='relative h-full min-h-0 w-full'>
        <LoadingOverlayV2 message='Đang tải...' />
      </div>
    );
  }

  return (
    <>
      <EntryForm
        open
        formMode={formMode}
        initialData={formInitialData}
        onClose={handleDialogClose}
        onSubmit={mutationState.handleSubmit}
        onAdd={() => {
          setIsCopyMode(false);
          setFormMode('add');
        }}
        onEdit={mutationState.handleEditClick}
        onDelete={() => setShowDelete(true)}
        onCopy={() => {
          setIsCopyMode(true);
          setFormMode('add');
        }}
      />
      <ConfirmationDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleConfirmDelete}
        selectedObj={selectedObj}
        title='Xóa chứng từ'
        message='Bạn có chắc chắn muốn xóa chứng từ này không?'
        confirmButtonText='Xóa'
        cancelButtonText='Hủy'
      />
    </>
  );
}

export default function DocumentEntry(props: DocumentEntryProps) {
  return (
    <>
      <DocumentEntryContent {...props} />
    </>
  );
}
